import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, User, CheckCircle2, AlertCircle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Spinner } from '../components/ui/spinner';
import { Field, FieldLabel } from '../components/ui/field';
import { Input, PasswordInput } from '../components/ui';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS } from '../config/api';
import axios from 'axios';

const InvitationAcceptance = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [step, setStep] = useState('loading'); // loading, form, success, error
  const [invitationData, setInvitationData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    password: '',
    confirmPassword: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  const token = searchParams.get('token');
  
  // Load invitation details on mount
  useEffect(() => {
    if (!token) {
      setStep('error');
      setError('Invalid invitation link - missing token');
      return;
    }
    
    loadInvitationDetails();
  }, [token]);
  
  const loadInvitationDetails = async () => {
    try {
      console.log('🔍 Loading invitation details for token:', token);

      const fullUrl = `${API_ENDPOINTS.invitation}/${token}`;
      console.log('🔍 Making request to:', fullUrl);

      const response = await axios.get(fullUrl);
      console.log('✅ API Response:', response);
      console.log('✅ Response data:', response.data);
      
      setInvitationData(response.data.data);
      setStep('form');
    } catch (err) {
      console.error('❌ API Error:', err);
      console.error('❌ Error response:', err.response);
      console.error('❌ Error data:', err.response?.data);
      
      setStep('error');
      setError(err.response?.data?.message || 'Failed to load invitation details');
    }
  };
  
  const handleInputChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    setError(''); // Clear error when user types
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    
    setIsSubmitting(true);
    setError('');

    try {
      const fullUrl = `${API_ENDPOINTS.invitation}/${token}/accept`;

      const response = await axios.post(fullUrl, {
        name: formData.name,
        password: formData.password
      });
      
      // Auto-login with the received token
      const { access_token } = response.data.data;
      localStorage.setItem('token', access_token);
      
      setStep('success');
      
      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
      
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to accept invitation');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Loading state
  if (step === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="text-center py-8">
            <Spinner className="mx-auto mb-4" />
            <p className="text-slate-400">Loading invitation...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (step === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="text-center py-8">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Invalid Invitation</h2>
            <p className="text-slate-400 mb-6">{error}</p>
            <Button onClick={() => navigate('/login')} variant="outline">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (step === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="text-center py-8">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Welcome to the Team!</h2>
            <p className="text-slate-400 mb-6">Your account has been created successfully. Redirecting to dashboard...</p>
            <Spinner className="mx-auto" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Form state
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mb-4">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <CardTitle className="text-white">Join the Team</CardTitle>
          <CardDescription className="text-slate-400">
            Complete your account setup
          </CardDescription>
        </CardHeader>

        <CardContent>
          {/* Invitation Details */}
          <div className="mb-6 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <h3 className="text-sm font-medium text-slate-300 mb-2">You've been invited to join:</h3>
            <div className="mb-3 inline-flex items-center px-4 py-2 bg-blue-600/20 border border-blue-600/30 rounded-full">
              <span className="text-blue-400 font-medium">{invitationData?.business?.name || 'Loading...'}</span>
            </div>
            <div className="space-y-1 text-sm text-slate-400">
              <p><span className="text-slate-300">Role:</span> {invitationData?.invitation?.role}</p>
              <p><span className="text-slate-300">Invited by:</span> {invitationData?.invited_by?.name}</p>
              <p><span className="text-slate-300">Email:</span> {invitationData?.invitation?.email}</p>
            </div>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <Field>
              <FieldLabel htmlFor="name">Full Name *</FieldLabel>
              <Input
                type="text"
                id="name"
                value={formData.name}
                onChange={handleInputChange('name')}
                placeholder="Enter your full name"
                leftIcon={User}
                required
                disabled={isSubmitting}
              />
            </Field>

            <Field>
              <FieldLabel htmlFor="password">Password *</FieldLabel>
              <PasswordInput
                id="password"
                value={formData.password}
                onChange={handleInputChange('password')}
                placeholder="Create a password (min 8 characters)"
                required
                disabled={isSubmitting}
              />
            </Field>

            <Field>
              <FieldLabel htmlFor="confirmPassword">Confirm Password *</FieldLabel>
              <PasswordInput
                id="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange('confirmPassword')}
                placeholder="Confirm your password"
                required
                disabled={isSubmitting}
              />
            </Field>

            <Button
              type="submit"
              disabled={!formData.name || !formData.password || !formData.confirmPassword || isSubmitting}
              className="w-full"
            >
              {isSubmitting ? (
                <>
                  <Spinner className="mr-2" />
                  Creating Account...
                </>
              ) : (
                'Accept Invitation & Create Account'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default InvitationAcceptance;